import { useEffect, useState } from "react";
import { Alert, Card, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { Column } from "react-table";
import { Grid } from "../../leap-components/Grid";
import LeapSearch from "./Search";
import { CreateStaticSelectColumnFilter } from "../../leap-components/Grid/FetchGrid/StaticSelectColumnFilter";
import { EpaGridOrderByFetchParams } from "../../leap-components/Grid/EpaGridProps";
import {
  IAuthorisationTypesList,
  ILicenceSearchList,
  GetLicenceAdvancedSearchFetchUrl,
  ILicenceSearchRow,
  ISectorsList,
  ICountyList,
  ILicenceStatusesList,
  YesNoAllFilterValues,
} from "../../api/ILicenceAdvancedSearch";
import { formatDate } from "../../api/DateUtils";
import { GetLicenceVersionUrl } from "../../external-apps/Config";
import { InformationIcon } from "../../leap-components/icons/LeapIcons";
import { MediaObj } from "../../hooks/useDetectScreenBreakpoints";

interface ISearchResultRow {
  licence_name: string;
  profile_number: string;
  organisation: string;
  active_application: string;
  county: string;
}

interface IAdvancedSearchProps {
  authorisationTypes: IAuthorisationTypesList;
  sectorsList: ISectorsList;
  statusesList: ILicenceStatusesList;
  countiesList: ICountyList;
  screenBreakpoints: MediaObj;
}

const GetLicenceVersionUrlFromRow = (row: any) => {
  return GetLicenceVersionUrl(
    row.original.authorisationtype,
    row.original.authorisationnumber.split("-")[0],
    row.original.licenceid,
    row.original.authorisationnumber
  );
};

const alertColor = "secondary";

const LeapAlert = () => (
  <Alert variant={alertColor} dismissible>
    <div className="alert-icon">
      <InformationIcon />
    </div>
    <div className="alert-message">
      <h4>
        <b>Advanced Search Function Overview</b>
      </h4>
      <p>
        The Advanced Search function enables users to search EPA licence
        applications and authorisation records. It provides access to
        summary-level information for each authorisation, and enables the user
        to apply filters such as status, sector, county, etc. to refine the
        results.
      </p>
      <p>
        <b>Important:</b> After applying filters, please clear them using{" "}
        <b>Clear All Filters</b> button below before beginning a new search to
        ensure correct results are returned.
      </p>
    </div>
  </Alert>
);

const AdvancedSearch = ({
  authorisationTypes,
  sectorsList,
  statusesList,
  countiesList,
  screenBreakpoints,
}: IAdvancedSearchProps) => {
  const [searchValue, setSearchValue] = useState("");

  const searchChangedFn = (newSearch: string) => {
    setSearchValue(newSearch);
  };

  const tableColumnsSmall: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
      sortType: "basic",
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
      sortType: "basic",
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countiesList.list.map((county) => county.value)
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
  ];

  const tableColumnsMedium: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
      sortType: "basic",
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
      sortType: "basic",
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "Sector",
      accessor: "sector",
      Filter: CreateStaticSelectColumnFilter(
        sectorsList.list.map((sector) => sector.value)
      ),
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countiesList.list.map((county) => county.value)
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
    {
      Header: "Final Authorisation Issued",
      accessor: "finalauthorisationissueddate",
      Filter: false,
      sortType: "basic",
    },
  ];

  const tableColumnsBig: Column<Record<string, ILicenceSearchRow>>[] = [
    {
      Header: "Number",
      accessor: "authorisationnumber",
      Filter: false,
      filter: "authorisationnumber",
      Cell: (rowInfo: any) => (
        <table>
          <tr>
            <td>
              <a
                href={GetLicenceVersionUrlFromRow(rowInfo.row)}
                target="_blank"
              >
                {rowInfo.value}
              </a>
            </td>
          </tr>
        </table>
      ),
      sortType: "basic",
    },
    {
      Header: "Name",
      accessor: "authorisationname",
      Filter: false,
    },
    {
      Header: "Authorisation Type",
      accessor: "authorisationtype",
      Filter: CreateStaticSelectColumnFilter(
        authorisationTypes.list.map((authType) => authType.value)
      ),
      sortType: "basic",
    },
    {
      Header: "Organisation",
      accessor: "organisation",
      Filter: false,
    },
    {
      Header: "Sector",
      accessor: "sector",
      Filter: CreateStaticSelectColumnFilter(
        sectorsList.list.map((sector) => sector.value)
      ),
    },
    {
      Header: "County",
      accessor: "county",
      Filter: CreateStaticSelectColumnFilter(
        countiesList.list.map((county) => county.value)
      ),
    },
    {
      Header: "Status",
      accessor: "status",
      Filter: CreateStaticSelectColumnFilter(
        statusesList.list.map((status) => status.value)
      ),
    },
    {
      Header: "Open for Submissions?",
      accessor: "openforsubmissions",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Open for Objections?",
      accessor: "openforobjections",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Priority Application this Year?",
      accessor: "pdduethisyear",
      Filter: CreateStaticSelectColumnFilter(
        YesNoAllFilterValues.map((filterValue) => filterValue)
      ),
    },
    {
      Header: "Final Authorisation Issued",
      accessor: "finalauthorisationissueddate",
      Filter: false,
      sortType: "basic",
    },
  ];

  let fetchUrlFn = (
    pageNo: number,
    recordsPerPage: number,
    searchText: string,
    filters: any,
    orderBy?: EpaGridOrderByFetchParams
  ) => {
    return GetLicenceAdvancedSearchFetchUrl(
      pageNo,
      recordsPerPage,
      searchText,
      filters,
      orderBy
    );
  };

  let formatResponseFunction = (searchResponse: ILicenceSearchList) => {
    const array = searchResponse.list.map((result) => {
      return {
        licenceid: result.licenceid,
        authorisationnumber: result.authorisationnumber,
        authorisationname: result.authorisationname,
        authorisationtype: result.authorisationtype,
        organisation: result.organisation,
        finalauthorisationissueddate: formatDate(
          result.finalauthorisationissueddate
        ),
        county: result.county,
        status: result.status,
        openforsubmissions: result.openforsubmissions,
        openforobjections: result.openforobjections,
        pdduethisyear: result.pdduethisyear,
        sector: result.sector,
      } as ILicenceSearchRow;
    });

    return {
      list: array,
      length: searchResponse.length,
      page: searchResponse.page,
      count: searchResponse.count,
    };
  };

  const orderBy = {
    orderByAscending: false,
    orderByName: "Number",
    enabledOrderByColumns: ["Number", "Name", "Final Authorisation Issued"],
  };

  const results = (
    <div>
      {screenBreakpoints.isSmall && (
        <Grid
          columns={tableColumnsSmall}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
          orderBy={orderBy}
        />
      )}
      {screenBreakpoints.isMedium && (
        <Grid
          columns={tableColumnsMedium}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
          orderBy={orderBy}
        />
      )}
      {(screenBreakpoints.isLarge || screenBreakpoints.isExtraLarge) && (
        <Grid
          columns={tableColumnsBig}
          data={[]}
          fetchUrlFunction={fetchUrlFn}
          enablePagination={true}
          enableQuickFind={false}
          formatResponseFunction={formatResponseFunction}
          searchText={searchValue}
          orderBy={orderBy}
        />
      )}
    </div>
  );

  const searchContent = (
    <div>
      <LeapAlert />
      <LeapSearch searchChangedFn={searchChangedFn} />
      {results}
    </div>
  );

  return searchContent;
};

export default AdvancedSearch;
